/** @file
 * @brief Base class for all panels. Panels are simple UI building blocks. Use this class to build other Panels.
 */
 
import EventEmitter from 'eventemitter3';

import type { PanelContainer } from '../PanelContainer';

/** @brief Panels are simple UI building blocks. Use this class to build other Panels.
 * @details The Panel class provides basic functionality for showing, hiding, and toggling visibility of panels. It also supports a "temporary mode" which can be enabled or disabled. Panels emit 'show' and 'hide' events when their visibility changes, allowing parent containers to react accordingly.
 * The class is designed to be extended by other panel types, which can implement additional functionality as needed. The update method can be overridden to allow panels to update their state based on changes in the application.
 */
abstract class Panel extends EventEmitter {
    public panelElement: HTMLElement;
    public parentObj: PanelContainer<string | symbol>;

    readonly id: string;
    protected temporary: boolean;

    /** @brief Creates panel.
     * @param {string} id Unique identifier for panel
     * @param {boolean} [hidden=false] If true the Panel will be created hidden
     * @param {HTMLElement} [panelElement] This element will be passed as element's root element
     */
    constructor(id: string, hidden = false, panelElement: HTMLElement = document.createElement("div")) {
        super();

        this.id = id;

        this.panelElement = panelElement;
        this.panelElement.setAttribute("id", id);

        if (hidden)
            this.panelElement.classList.add('hidden');
    }

    /** @brief Makes panel visible, triggers 'show' event. */
    show(): void {
        if (!this.isHidden())
            return;

        this.panelElement.classList.remove("hidden");

        if (this.parentObj)
            this.parentObj._onPanelShow(this);

        this.emit('show');
    }

    /** @brief Makes panel hidden, triggers 'hide' event. */
    hide(): void {
        if (this.isHidden())
            return;

        this.panelElement.classList.add("hidden");
        if (this.parentObj)
            this.parentObj._onPanelHide(this);

        this.emit('hide');
    }

    /** @brief Toggles panel's visibility. */
    toggle(): void {
        if (this.isHidden())
            this.show();
        else
            this.hide();
    }

    /** @returns {boolean} True if panel is currently hidden. */
    isHidden(): boolean {
        return this.panelElement.classList.contains("hidden");
    }

    /** @brief Enables "temporary mode" for panel. */
    enableTemporaryMode(): void {
        this.temporary = true;
        this.panelElement.classList.add('temporary-panel');
    }

    /** @brief Disables "temporary mode" for panel. */
    disableTemporaryMode() : void {
        this.temporary = false;
        this.panelElement.classList.remove('temporary-panel');
    }

    /** @returns {boolean} True if panel is in "temporary mode". */
    isTemporary(): boolean {
        return this.temporary;
    }

    /** @brief Updates Panel's state from old state to some new state.
     * @param {object} newState Object with old state.
     * @param {object} oldState Object with new state.
     */
    // eslint-disable-next-line unused-imports/no-unused-vars
    update(_newState: unknown, _oldState: unknown) : void {
        return;
    }
}

export { Panel };